import GuideLayout from '../../layouts/GuideLayout';
import type { TocItem } from '../../layouts/GuideLayout';
import { AffiliateCTA } from '../../components/content/AffiliateCTA';
import { ResourceLink } from '../../components/content/ResourceLink';
import { CalculatorEmbed } from '../../components/content/CalculatorEmbed';

const tocItems: TocItem[] = [
  { id: 'core-difference', label: 'The Core Difference', level: 2 },
  { id: 'search-jobs', label: 'Search Jobs Explained', level: 2 },
  { id: 'restore', label: 'Restore Explained', level: 2 },
  { id: 'decision-framework', label: 'Decision Framework', level: 2 },
  { id: 'calculator', label: 'Cost Comparison Calculator', level: 2 },
  { id: 'common-mistakes', label: 'Common Mistakes', level: 2 },
  { id: 'resources', label: 'Official Resources', level: 2 },
];

const bottomCta = (
  <AffiliateCTA
    programme="Pluralsight"
    courseName="SC-200 Security Operations Analyst"
    url="https://www.pluralsight.com/paths/sc-200-microsoft-security-operations-analyst-associate"
    price="From £29/month"
    badgeText="Covers Search Jobs & Restore"
  />
);

export default function SearchJobVsRestorePage() {
  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: 'Azure Log Analytics: Search Job vs Restore',
            description:
              'Search Job vs Restore cost comparison for Azure Log Analytics UK 2026.',
            author: { '@type': 'Organization', name: 'AzureCalc.uk' },
            publisher: {
              '@type': 'Organization',
              name: 'AzureCalc.uk',
              url: 'https://www.azure-calc.co.uk',
            },
            dateModified: '2026-03-30',
            url: 'https://www.azure-calc.co.uk/guides/log-analytics-search-job-vs-restore',
          }),
        }}
      />
      <GuideLayout
        title="Azure Log Analytics: Search Job vs Restore — Cost Comparison 2026"
        description="Azure Log Analytics Search Job vs Restore cost comparison UK 2026. Search Jobs cost £0.0046/GB scanned. Restore has a 2TB minimum — restoring 1GB costs £189/day. Understand when to use each."
        lastUpdated="March 2026"
        tocItems={tocItems}
        relatedCalculators={[
          { service: 'log-analytics', path: '/log-analytics', label: 'Log Analytics Calculator' },
        ]}
        bottomCta={bottomCta}
      >
        <p className="text-[15px] italic mb-8" style={{ color: 'var(--text-tertiary)' }}>
          Search Jobs cost pennies. Restore can cost hundreds of pounds. Here is how to choose the
          right option and avoid the 2TB minimum billing trap.
        </p>

        <p className="text-[12px] italic mb-6" style={{ color: 'var(--text-tertiary)' }}>
          Built and verified by an independent Azure engineer, frustrated with the official calculator.
        </p>

        {/* SECTION 1 */}
        <section id="core-difference">
          <h2>The Core Difference</h2>
          <div className="not-prose grid grid-cols-1 sm:grid-cols-2 gap-4 my-6">
            {/* Search Job */}
            <div className="border rounded p-5" style={{ backgroundColor: 'var(--surface-container-low)', borderColor: '#0078d4' }}>
              <div className="flex items-center gap-2 mb-3">
                <span className="material-symbols-outlined text-[#0078d4] text-[20px]">search</span>
                <p className="text-[14px] font-bold" style={{ color: '#c7e0f4' }}>Search Job</p>
              </div>
              <ul className="flex flex-col gap-2">
                {[
                  'Query archived data asynchronously',
                  'Cost: £0.0046/GB scanned',
                  'No minimum charge',
                  'Results written to _SRCH table',
                  'Best for: ad-hoc investigations',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2 text-[13px]" style={{ color: 'var(--text-secondary)' }}>
                    <span className="text-[#0078d4] shrink-0 mt-0.5">›</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Restore */}
            <div className="border rounded p-5 bg-[#a4262c]/10 border-[#a4262c]/40">
              <div className="flex items-center gap-2 mb-3">
                <span className="material-symbols-outlined text-[#e04040] text-[20px]">restore</span>
                <p className="text-[14px] font-bold text-[#a4262c]">Restore</p>
              </div>
              <ul className="flex flex-col gap-2">
                {[
                  'Bring archived data into hot cache',
                  'Cost: £0.0924/GB/day',
                  '2TB MINIMUM — always billed as at least 2,048 GB',
                  'Best for: sustained heavy querying of large datasets',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2 text-[13px]" style={{ color: 'var(--text-secondary)' }}>
                    <span className="text-[#a4262c] shrink-0 mt-0.5">›</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* SECTION 2 */}
        <section id="search-jobs">
          <h2>Search Jobs Explained</h2>
          <p>
            A Search Job runs asynchronously against your archived (Basic or Auxiliary tier) log
            data. Rather than returning results immediately, it scans the specified time range and
            writes matching records into a new table named{' '}
            <code>OriginalTable_SRCH</code>. You can then query that results table using normal KQL
            at no additional scan cost. Results tables have 31-day interactive retention.
          </p>
          <p>
            Search Jobs are charged per GB scanned across the query time range — not per GB
            returned. If your table ingests 5 GB/day and you search 7 days, you are charged for
            35 GB regardless of how many records match your query. Results are also ingested at
            the standard Analytics Logs rate (£2.13/GB).
          </p>

          <h3>Worked Example 1 — Small Investigation</h3>
          <div className="not-prose border rounded p-5 my-4" style={{ backgroundColor: 'var(--surface-container)', borderColor: 'var(--outline-variant)' }}>
            <p className="text-[13px] font-bold text-[#0078d4] uppercase tracking-wide mb-3">
              OfficeActivity — 7-day search
            </p>
            <table className="w-full text-[13px]">
              <tbody style={{ borderTop: '1px solid var(--surface-container-high)' }} className="divide-y">
                {[
                  ['Daily ingestion', '5 GB/day'],
                  ['Search range', '7 days'],
                  ['GB scanned', '5 × 7 = 35 GB'],
                  ['Search cost', '35 × £0.0046 = £0.16'],
                  ['Result set', '0.5 GB'],
                  ['Result ingestion', '0.5 × £2.13 = £1.07'],
                ].map(([k, v]) => (
                  <tr key={k} style={{ borderBottom: '1px solid var(--surface-container-high)' }}>
                    <td className="py-1.5 w-40" style={{ color: 'var(--text-tertiary)' }}>{k}</td>
                    <td className="py-1.5" style={{ color: 'var(--text-secondary)' }}>{v}</td>
                  </tr>
                ))}
                <tr style={{ borderTop: '2px solid var(--outline-variant)' }}>
                  <td className="py-2 font-bold" style={{ color: 'var(--text-primary)' }}>Total</td>
                  <td className="py-2 font-bold text-[#0078d4]">£1.23</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h3>Worked Example 2 — Large Investigation</h3>
          <div className="not-prose border rounded p-5 my-4" style={{ backgroundColor: 'var(--surface-container)', borderColor: 'var(--outline-variant)' }}>
            <p className="text-[13px] font-bold text-[#0078d4] uppercase tracking-wide mb-3">
              SecurityEvent — 90-day search
            </p>
            <table className="w-full text-[13px]">
              <tbody>
                {[
                  ['Daily ingestion', '50 GB/day'],
                  ['Search range', '90 days'],
                  ['GB scanned', '50 × 90 = 4,500 GB'],
                  ['Search cost', '4,500 × £0.0046 = £20.70'],
                  ['Result set', '2 GB'],
                  ['Result ingestion', '2 × £2.13 = £4.26'],
                ].map(([k, v]) => (
                  <tr key={k} style={{ borderBottom: '1px solid var(--surface-container-high)' }}>
                    <td className="py-1.5 w-40" style={{ color: 'var(--text-tertiary)' }}>{k}</td>
                    <td className="py-1.5" style={{ color: 'var(--text-secondary)' }}>{v}</td>
                  </tr>
                ))}
                <tr style={{ borderTop: '2px solid var(--outline-variant)' }}>
                  <td className="py-2 font-bold" style={{ color: 'var(--text-primary)' }}>Total</td>
                  <td className="py-2 font-bold text-[#0078d4]">£24.96</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* SECTION 3 */}
        <section id="restore">
          <h2>Restore Explained</h2>
          <p>
            Restore brings a specified time range of archived data into a hot cache, creating a
            table named <code>OriginalTable_RST</code>. Unlike Search Jobs, restore is synchronous
            — data is available for full KQL querying as soon as the restore completes. There is no
            charge for querying restored data once it is in the hot cache.
          </p>
          <p>
            Restore is charged at £0.0924/GB/day with a 2TB (2,048 GB) minimum and a 12-hour
            minimum duration. Billing is per UTC day and continues until you explicitly dismiss the
            restore. Forgetting to dismiss is one of the most common causes of unexpected Azure
            bills.
          </p>

          <div className="not-prose bg-[#a4262c]/10 border-2 border-[#a4262c]/50 rounded-lg p-5 my-6">
            <div className="flex items-start gap-3">
              <span className="material-symbols-outlined text-red-400 text-[22px] shrink-0 mt-0.5">
                warning
              </span>
              <div>
                <p className="text-[14px] font-bold text-[#a4262c] mb-2">The 2TB Minimum Billing Trap</p>
                <p className="text-[13px] mb-3" style={{ color: 'var(--text-secondary)' }}>
                  The 2TB minimum means restoring <strong>any</strong> amount of data under 2TB
                  costs the same. The minimum only stops mattering above 2TB.
                </p>
                <div className="flex flex-col gap-1">
                  {[
                    ['Restoring 1 GB', '2,048 GB billed', '£189.24/day'],
                    ['Restoring 500 GB', '2,048 GB billed', '£189.24/day'],
                    ['Restoring 2,049 GB', '2,049 GB billed', '£189.33/day'],
                  ].map(([size, billed, cost]) => (
                    <div
                      key={size}
                      className="flex items-center gap-3 text-[13px]"
                      style={{ color: 'var(--text-secondary)' }}
                    >
                      <span className="w-32 shrink-0">{size}</span>
                      <span className="text-red-400">→</span>
                      <span className="w-36 shrink-0">{billed}</span>
                      <span className="font-bold text-[#a4262c]">{cost}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <h3>Worked Example 1 — Small Restore (The Trap)</h3>
          <div className="not-prose rounded p-5 my-4" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid rgba(224,83,83,0.6)' }}>
            <p className="text-[13px] font-bold text-[#e05353] uppercase tracking-wide mb-3">
              You want 3 GB — Azure bills 2,048 GB
            </p>
            <table className="w-full text-[13px]">
              <tbody>
                {[
                  ['Data you want', '3 GB of OfficeActivity logs'],
                  ['Azure bills', '2,048 GB (2TB minimum)'],
                  ['Duration', '3 days'],
                  ['Restore cost', '2,048 × £0.0924 × 3 = £567.71'],
                  ['Same via Search Job', '£0.16 + £0.14 = £0.30'],
                ].map(([k, v]) => (
                  <tr key={k} style={{ borderBottom: '1px solid var(--surface-container-high)' }}>
                    <td className="py-1.5 w-40" style={{ color: 'var(--text-tertiary)' }}>{k}</td>
                    <td className="py-1.5" style={{ color: 'var(--text-secondary)' }}>{v}</td>
                  </tr>
                ))}
                <tr style={{ borderTop: '2px solid rgba(224,83,83,0.6)' }}>
                  <td className="py-2 font-bold" style={{ color: 'var(--text-primary)' }}>Difference</td>
                  <td className="py-2 font-bold text-[#e05353]">£567.41 wasted</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h3>Worked Example 2 — Large Restore (Justified)</h3>
          <div className="not-prose border rounded p-5 my-4" style={{ backgroundColor: 'var(--surface-container)', borderColor: 'var(--outline-variant)' }}>
            <p className="text-[13px] font-bold text-[#0078d4] uppercase tracking-wide mb-3">
              5,000 GB SecurityEvent — sustained 14-day investigation
            </p>
            <table className="w-full text-[13px]">
              <tbody>
                {[
                  ['Data to restore', '5,000 GB (above 2TB minimum)'],
                  ['Duration', '14 days'],
                  ['Restore cost', '5,000 × £0.0924 × 14 = £6,468'],
                  ['Via Search Job (once)', '5,000 × £0.0046 = £23'],
                ].map(([k, v]) => (
                  <tr key={k} style={{ borderBottom: '1px solid var(--surface-container-high)' }}>
                    <td className="py-1.5 w-40" style={{ color: 'var(--text-tertiary)' }}>{k}</td>
                    <td className="py-1.5" style={{ color: 'var(--text-secondary)' }}>{v}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-[12px] mt-3 italic" style={{ color: 'var(--text-tertiary)' }}>
              If you need to run 200+ KQL queries against this data over 14 days, Restore justifies
              its cost — a Search Job only runs once and results expire after 31 days.
            </p>
          </div>
        </section>

        {/* SECTION 4 */}
        <section id="decision-framework">
          <h2>The Decision Framework</h2>
          <div className="not-prose grid grid-cols-1 sm:grid-cols-3 gap-4 my-6">
            {/* Use Search Job */}
            <div className="rounded p-4 bg-[#107c10]/10 border border-[#107c10]/30">
              <p className="text-[12px] font-bold text-[#107c10] uppercase tracking-wide mb-3">
                Use Search Job when
              </p>
              <ul className="flex flex-col gap-2">
                {[
                  'Ad-hoc investigation (1–5 queries)',
                  'Data is under 2TB',
                  'You can wait for async results',
                  'SOC analyst investigating a specific incident',
                  'Budget is a concern',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2 text-[13px]" style={{ color: 'var(--text-secondary)' }}>
                    <span className="text-[#107c10] shrink-0 mt-0.5">✓</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Use Restore */}
            <div className="rounded p-4" style={{ backgroundColor: 'var(--surface-container-low)', border: '1px solid rgba(0,120,212,0.5)' }}>
              <p className="text-[12px] font-bold text-[#0078d4] uppercase tracking-wide mb-3">
                Use Restore when
              </p>
              <ul className="flex flex-col gap-2">
                {[
                  'Sustained investigation over many days',
                  'You need to run 50+ queries on same data',
                  'Data is over 2TB (above minimum anyway)',
                  'You need the full KQL timeout',
                  'Performance matters more than cost',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2 text-[13px]" style={{ color: 'var(--text-secondary)' }}>
                    <span className="text-[#0078d4] shrink-0 mt-0.5">✓</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Never use Restore */}
            <div className="rounded p-4 bg-[#a4262c]/10 border border-[#a4262c]/30">
              <p className="text-[12px] font-bold text-[#a4262c] uppercase tracking-wide mb-3">
                Never use Restore when
              </p>
              <ul className="flex flex-col gap-2">
                {[
                  'You only need to run 1–3 queries',
                  'Your data is under 2TB',
                  'You are doing a quick incident triage',
                  'A Search Job can answer the question',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2 text-[13px]" style={{ color: 'var(--text-secondary)' }}>
                    <span className="text-[#a4262c] shrink-0 mt-0.5">✗</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* SECTION 5 */}
        <section id="calculator">
          <h2>Cost Comparison Calculator</h2>
          <p>
            Use our free Log Analytics calculator to compare exact Search Job vs Restore costs for
            your specific scenario. Use the Search Job tab for ad-hoc queries, and the Restore tab
            to see the 2TB minimum impact on your specific data volume.
          </p>
          <div className="not-prose mt-4">
            <CalculatorEmbed service="log-analytics" />
          </div>
        </section>

        {/* SECTION 6 */}
        <section id="common-mistakes">
          <h2>Common Mistakes</h2>
          <div className="not-prose flex flex-col gap-4 my-6">
            {[
              {
                n: '01',
                title: 'Restoring for a single investigation',
                body: 'The most expensive mistake in Azure. Always try a Search Job first. A Search Job costs fractions of a penny per GB scanned — there is almost never a reason to Restore for a single incident investigation.',
              },
              {
                n: '02',
                title: 'Forgetting to dismiss the restore',
                body: 'Restore bills every UTC day until explicitly dismissed. A restore left running for a week costs 7× the daily rate. Set a calendar reminder when you initiate any restore.',
              },
              {
                n: '03',
                title: 'Assuming restore cost equals data size',
                body: 'The 2TB minimum means small restores are disproportionately expensive. Restoring 100 MB and restoring 1,900 GB cost exactly the same per day: £189.24.',
              },
              {
                n: '04',
                title: 'Not checking table ingestion volume before a Search Job',
                body: 'Search Job cost depends on daily ingestion rate × days searched, not query result size. A table ingesting 100 GB/day costs £0.46 to scan for one day regardless of how many records your filter matches.',
              },
            ].map((m) => (
              <div
                key={m.n}
                className="flex gap-4 border rounded p-4"
                style={{ backgroundColor: 'var(--surface-container)', borderColor: 'var(--outline-variant)' }}
              >
                <span className="text-[28px] font-black shrink-0 leading-none mt-0.5 select-none" style={{ color: 'var(--outline-variant)' }}>
                  {m.n}
                </span>
                <div>
                  <p className="text-[14px] font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>{m.title}</p>
                  <p className="text-[13px] leading-relaxed" style={{ color: 'var(--text-tertiary)' }}>{m.body}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* SECTION 7 */}
        <section id="resources">
          <h2>Official Resources</h2>
          <div className="not-prose flex flex-col gap-2 mb-6">
            <ResourceLink
              title="Search jobs in Azure Monitor Logs"
              url="https://learn.microsoft.com/en-us/azure/azure-monitor/logs/search-jobs"
              source="Microsoft Learn"
            />
            <ResourceLink
              title="Restore logs in Azure Monitor"
              url="https://learn.microsoft.com/en-us/azure/azure-monitor/logs/restore"
              source="Microsoft Learn"
            />
            <ResourceLink
              title="Azure Monitor pricing"
              url="https://azure.microsoft.com/en-gb/pricing/details/monitor/"
              source="Microsoft Azure"
            />
          </div>
          <p>
            SC-200 covers Search Jobs and Restore in the Sentinel investigation modules — it is the
            most relevant certification for analysts who regularly query archived log data.
          </p>
          <div className="not-prose mt-4">
            <AffiliateCTA
              programme="Pluralsight"
              courseName="SC-200 Security Operations Analyst"
              url="https://www.pluralsight.com/paths/sc-200-microsoft-security-operations-analyst-associate"
              price="From £29/month"
              badgeText="Covers Search Jobs & Restore"
            />
          </div>
        </section>
      </GuideLayout>
    </>
  );
}
